// components/Posts/SpillTheTeaModal.tsx
import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import BaseModal from "ui/Modal";
import IssueSearch, { Suggestion } from "components/Issues/IssueSearch";
import { Impact, Issue, Action, Solution, Comment } from "util/constants";
import ImpactPostForm, { PostFormHandle } from "components/Posts/ImpactPostForm";
import TeabatePostForm, { TeabateFormHandle } from "components/Posts/TeabatePostForm";
import SolutionPostForm, { SolutionFormHandle } from "components/Posts/SolutionPostForm";
import ActionPostForm, { ActionFormHandle } from "components/Posts/ActionPostForm";
import CollectionPostForm, { CollectionFormHandle } from "components/Posts/CollectionPostForm";
import ThreadFormBase, { ThreadFormHandle } from "components/Posts/forms/ThreadFormBase";
import { Loader2 } from "lucide-react";
import { SPILL_KIND_ROWS, type SpillPostType } from "components/Posts/postKindConfig";
import type { CollectionPostResponse } from "util/posts";
import DraftsList from "components/Drafts/DraftsList";
import type { DbDraft } from "hooks/queries/useDraftsQuery";
import type { LocalDraftSummary } from "util/localDrafts";
import { getDraftUiStrings, resolveDraftLocale } from "i18n/draftUi";
import DraftSaveDialog from "ui/DraftSaveDialog";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Thread success passes a `Comment`; other spill types pass `Impact` (legacy name). */
  onImpactAdded?: (item: Impact | Comment) => void;
  onSolutionAdded?: (solution: Solution) => void;
  onActionAdded?: (action: Action) => void;
  onCollectionAdded?: (collection: CollectionPostResponse) => void;
  initialSelectedIssue?: Suggestion | Issue | null;
  customTitle?: string;
  fixedPostType?: SpillPostType | null;
  hidePostTypeButtons?: boolean;
  /** Merged into collection `properties` when creating a collection post. */
  collectionExtraProperties?: Record<string, unknown>;
  teabateNavigateOnSuccess?: boolean;
  teabateRequireAlignmentDimension?: boolean;
  teabateDimensionRequiredMessage?: string;
  teabateInitialClaim?: string;
}

const SpillTheTeaModal: React.FC<Props> = ({
  open,
  onClose,
  onImpactAdded,
  onSolutionAdded,
  onActionAdded,
  onCollectionAdded,
  initialSelectedIssue = null,
  customTitle = "Spill the tea",
  fixedPostType = null,
  hidePostTypeButtons = false,
  collectionExtraProperties,
  teabateNavigateOnSuccess = true,
  teabateRequireAlignmentDimension = false,
  teabateDimensionRequiredMessage,
  teabateInitialClaim,
}) => {
  const navigate = useNavigate();
  const [selectedIssue, setSelectedIssue] = useState<Suggestion | Issue | null>(initialSelectedIssue);
  const [selectedType, setSelectedType] = useState<SpillPostType | null>(fixedPostType);
  const [error, setError] = useState<string>("");
  const [canSubmit, setCanSubmit] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [saveDraftDialogOpen, setSaveDraftDialogOpen] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [localDraftValue, setLocalDraftValue] = useState<Record<string, unknown> | null>(null);
  const draftUi = getDraftUiStrings(resolveDraftLocale());

  const handleSelectIssue = useCallback((suggestion: Suggestion) => {
    setSelectedIssue(suggestion);
  }, []);

  const clearState = () => {
    setSelectedType(null);
    setLocalDraftValue(null);
    setError("");
    setCanSubmit(false);
    setIsSubmitting(false);
  };

  const handleClosePicker = useCallback(() => {
    clearState();
    onClose();
  }, [onClose]);

  const shouldPromptForDbDraft = selectedType === "action" || selectedType === "collection";

  const handleRequestClosePicker = useCallback(() => {
    if (shouldPromptForDbDraft && canSubmit) {
      setSaveDraftDialogOpen(true);
      return;
    }
    handleClosePicker();
  }, [canSubmit, handleClosePicker, shouldPromptForDbDraft]);

  const threadRef = useRef<ThreadFormHandle>(null);
  const impactRef = useRef<PostFormHandle>(null);
  const teabateRef = useRef<TeabateFormHandle>(null);
  const solutionRef = useRef<SolutionFormHandle>(null);
  const actionRef = useRef<ActionFormHandle>(null);
  const collectionRef = useRef<CollectionFormHandle>(null);

  const submitByType: Partial<Record<SpillPostType, () => void>> = {
    thread: () => threadRef.current?.submit(),
    impact: () => impactRef.current?.submit(),
    teabate: () => teabateRef.current?.submit(),
    solution: () => solutionRef.current?.submit(),
    action: () => actionRef.current?.submit(),
    collection: () => collectionRef.current?.submit(),
  };

  const saveDraftByType: Partial<Record<SpillPostType, () => Promise<void>>> = {
    action: () => actionRef.current?.saveDraft() ?? Promise.resolve(),
    collection: () => collectionRef.current?.saveDraft() ?? Promise.resolve(),
  };

  const discardDraftByType: Partial<Record<SpillPostType, () => void>> = {
    action: () => actionRef.current?.discardDraft(),
    collection: () => collectionRef.current?.discardDraft(),
  };

  const footer = (
    <div className="flex flex-grow flex-col py-2 justify-end place-items-end mb-0 mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row">
      <button type="button" onClick={handleRequestClosePicker} className="btn-secondary w-full sm:w-fit py-2 px-4">
        Cancel
      </button>
      <button
        type="button"
        disabled={!canSubmit || isSubmitting}
        onClick={() => {
          if (isSubmitting) return;
          if (selectedType) submitByType[selectedType]?.();
        }}
        className={`w-full sm:w-fit py-2 px-4 ${
          !canSubmit ? "btn-primary-disabled opacity-50 cursor-not-allowed" : "btn-primary"
        }`}
      >
        {isSubmitting ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" aria-hidden />
          </span>
        ) : (
          "Submit"
        )}
      </button>
    </div>
  );

  const closeAfter =
    <T,>(cb?: (x: T) => void) =>
    (x: T) => {
      cb?.(x);
      handleClosePicker();
    };

  const closeAfterCollection = (collection: CollectionPostResponse) => {
    onCollectionAdded?.(collection);
    handleClosePicker();
    if (onCollectionAdded) {
      return;
    }
    const collectionType =
      typeof collection.properties?.collection_type === "string"
        ? collection.properties.collection_type
        : "standard";
    if (collectionType === "graph") {
      navigate(`/graph?mode=edit&collectionId=${encodeURIComponent(String(collection.id))}`);
      return;
    }
    navigate(`/collection/${encodeURIComponent(String(collection.id))}`);
  };

  const handleOpenDraft = (draft: DbDraft | LocalDraftSummary) => {
    if (draft.source === "local") {
      if (draft.issue_id) {
        setSelectedIssue({
          id: draft.issue_id,
          name: `Issue #${draft.issue_id}`,
          properties: {},
        } as Issue);
      }
      if (draft.kind === "thread") {
        setLocalDraftValue(draft.value);
        setSelectedType("thread");
        return;
      }
      if (draft.entityType === "action" || draft.entityType === "collection") {
        setLocalDraftValue(draft.value);
        setSelectedType(draft.entityType);
        return;
      }
    }

    if (draft.draftType === "comment") {
      navigate(`/comment/${encodeURIComponent(String(draft.id))}`);
      handleClosePicker();
      return;
    }
    if (draft.entityType === "collection") {
      const props = draft.source === "db" && draft.properties && typeof draft.properties === "object" ? draft.properties : {};
      const collectionType = typeof props.collection_type === "string" ? props.collection_type : "standard";
      navigate(
        collectionType === "graph"
          ? `/graph?mode=edit&collectionId=${encodeURIComponent(String(draft.id))}`
          : `/collection/${encodeURIComponent(String(draft.id))}`
      );
      handleClosePicker();
      return;
    }
    navigate(`/${draft.entityType}/${encodeURIComponent(String(draft.id))}`);
    handleClosePicker();
  };

  return (
    <BaseModal
      isOpen={open}
      onClose={handleRequestClosePicker}
      title={customTitle}
      footer={selectedType === "thread" ? undefined : footer}
      stickyFooter
    >
      <DraftSaveDialog
        isOpen={saveDraftDialogOpen}
        title={draftUi.cancelSaveTitle}
        description={draftUi.cancelSaveBody(selectedType || "post")}
        discardText={draftUi.discard}
        saveText={draftUi.save}
        isSaving={isSavingDraft}
        onDiscard={() => {
          if (selectedType) {
            discardDraftByType[selectedType]?.();
          }
          setSaveDraftDialogOpen(false);
          handleClosePicker();
        }}
        onSave={() => {
          if (!selectedType) {
            return;
          }
          setIsSavingDraft(true);
          void saveDraftByType[selectedType]?.()
            .then(() => {
              setSaveDraftDialogOpen(false);
              handleClosePicker();
            })
            .finally(() => setIsSavingDraft(false));
        }}
        onDismiss={() => setSaveDraftDialogOpen(false)}
      />
      <div className="flex flex-col space-y-4" onClick={(e) => e.stopPropagation()}>
        {!hidePostTypeButtons ? (
          <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
            {selectedType === "collection"
              ? "Optionally attach this collection to an issue for extra context."
              : "Search and select an issue: anything that affects society-directly or indirectly through influence."}
          </p>
        ) : null}

        <div className="form-control mb-0">
          <IssueSearch
            inputType="add"
            placeholder="Issue"
            className="w-full"
            fieldRequired={selectedType !== "collection"}
            prepopulateData={selectedIssue}
            validationState="none"
            onSelect={handleSelectIssue}
            onEntityChipClose={() => setSelectedIssue(null)}
          />
        </div>

        {!hidePostTypeButtons ? (
          <div className="form-control">
            <label className="block text-sm font-semibold mb-2">What kind of spill is this?</label>
            <div className="flex flex-wrap gap-2">
              {SPILL_KIND_ROWS.map(({ key, label, Icon, activeClass }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setSelectedType(key);
                    setLocalDraftValue(null);
                    setCanSubmit(false);
                  }}
                  className={`px-2 py-1.5 text-xs rounded-full transition-colors flex items-center gap-1 ${
                    selectedType === key ? activeClass : "btn-secondary font-normal"
                  }`}
                >
                  <Icon className="size-4" aria-hidden />
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {!hidePostTypeButtons && selectedType === null ? (
          <DraftsList
            compact
            heading={draftUi.postDraftsHeading}
            filters={{ source: "post", limit: 8 }}
            onOpenDraft={handleOpenDraft}
          />
        ) : null}

        {error ? (
          <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
            <span>{error}</span>
          </div>
        ) : null}

        {selectedType === "thread" ? (
          <ThreadFormBase
            ref={threadRef}
            entity={selectedIssue as Issue | Suggestion | null}
            onSuccess={closeAfter(onImpactAdded)}
            onCancel={handleClosePicker}
            hideActions={true}
            onCanSubmitChange={setCanSubmit}
          />
        ) : null}

        {selectedType === "impact" ? (
          <ImpactPostForm
            ref={impactRef}
            entity={selectedIssue as Issue | Suggestion | null}
            onSuccess={closeAfter(onImpactAdded)}
            onCancel={handleClosePicker}
            hideActions={true}
            onCanSubmitChange={setCanSubmit}
            onSubmittingChange={setIsSubmitting}
          />
        ) : null}

        {selectedType === "teabate" ? (
          <TeabatePostForm
            ref={teabateRef}
            entity={selectedIssue as Issue | Suggestion | null}
            onSuccess={closeAfter(onImpactAdded)}
            onCancel={handleClosePicker}
            hideActions={true}
            onCanSubmitChange={setCanSubmit}
            onSubmittingChange={setIsSubmitting}
            navigateOnSuccess={teabateNavigateOnSuccess}
            requireAlignmentDimension={teabateRequireAlignmentDimension}
            dimensionRequiredMessage={teabateDimensionRequiredMessage}
            initialTitle={teabateInitialClaim}
          />
        ) : null}

        {selectedType === "solution" ? (
          <SolutionPostForm
            ref={solutionRef}
            entity={selectedIssue as Issue | Suggestion | null}
            onSuccess={closeAfter(onSolutionAdded)}
            onCancel={handleClosePicker}
            hideActions={true}
            onCanSubmitChange={setCanSubmit}
            onSubmittingChange={setIsSubmitting}
          />
        ) : null}

        {selectedType === "action" ? (
          <ActionPostForm
            ref={actionRef}
            entity={selectedIssue as Issue | Suggestion | null}
            onSuccess={closeAfter(onActionAdded)}
            onCancel={handleClosePicker}
            hideActions={true}
            onCanSubmitChange={setCanSubmit}
            onSubmittingChange={setIsSubmitting}
            initialDraftValue={localDraftValue}
          />
        ) : null}

        {selectedType === "collection" ? (
          <CollectionPostForm
            ref={collectionRef}
            entity={selectedIssue as Issue | Suggestion | null}
            onSuccess={closeAfterCollection}
            onCancel={handleClosePicker}
            hideActions={true}
            onCanSubmitChange={setCanSubmit}
            onSubmittingChange={setIsSubmitting}
            extraCollectionProperties={collectionExtraProperties}
            initialDraftValue={localDraftValue}
          />
        ) : null}
      </div>
    </BaseModal>
  );
};

export default SpillTheTeaModal;
